/**
 * Settings > Workspaces – list workspaces, create, edit (name/icon/color/type).
 * Phase E: prototype-aligned UI with breadcrumb drill-in, OrgIndicator, icon/color pickers.
 */

import { useState, useEffect } from 'react'
import {
  User,
  Building2,
  Plus,
  ChevronRight,
  Settings as SettingsIcon,
  Loader2,
  UserPlus,
  Copy,
  Trash2,
  MoreHorizontal,
  Terminal,
  Inbox,
  Check,
  AlertTriangle,
} from 'lucide-react'
import { WORKSPACE_ICONS, getWorkspaceIcon } from '@/lib/workspace-icons'
import {
  DEFAULT_WORKSPACE_COLOR,
  normalizeWorkspaceHex,
} from '@/lib/workspace-color'
import { WorkspaceColorPicker } from '@/components/features/settings/workspace-color-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SettingsSection } from '@/components/features/settings/settings-section'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { fetchWithAuth, API_BASE_URL } from '@/lib/api'
import { openInboxThread } from '@/lib/cloud-dispatch'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { useWorkspace } from '@/providers/workspace-provider'
import { useWorkspaceExtensions } from '@/hooks/use-workspace-extensions'
import { useBasecampAuth } from '@/hooks/use-basecamp'
import { BasecampListeningPanel } from '@/components/integrations/basecamp/basecamp-listening-panel'
import { BasecampProjectFolderMappingsCard } from '@/components/integrations/basecamp/basecamp-project-folder-mappings-card'
import { ExtensionSettingsShell } from '@/components/integrations/_shared/ExtensionSettingsShell'
import { useGoogleDriveAuth } from '@/hooks/use-google-drive-auth'
import { WorkspaceSecrets } from './workspace-secrets-settings'
import { WorkspaceUsage } from './workspace-usage-settings'
import { ZoomIntegrationSection } from '@/components/integrations/zoom/ZoomIntegrationSection'
import { SmtpIntegrationSection } from '@/components/integrations/smtp/SmtpIntegrationSection'
import { UptimeIntegrationSection } from '@/components/integrations/uptime/UptimeIntegrationSection'
import { NextcloudIntegrationSection } from '@/components/integrations/nextcloud/NextcloudIntegrationSection'
import { SlackIntegrationSection } from '@/components/integrations/slack/SlackIntegrationSection'
import { DrivePublishTemplateCard } from '@/components/integrations/google-workspace/drive-publish-template-card'
import {
  EXTENSION_LABELS,
  EXTENSION_DESCRIPTIONS,
  EXTENSIONS_WITH_SETUP,
  WORKSPACE_SECTIONS,
  parseFocusSection,
  type WorkspaceSectionId,
} from '@/lib/settings-nav'
import { cn } from '@/lib/utils'
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'

type OrgType = 'personal' | 'work' | 'client' | 'community' | 'other'

interface ApiOrg {
  id: string
  name: string
  slug: string
  type: string
  icon: string | null
  color: string | null
  content_root: string | null
  show_in_sidebar: boolean
  template: string | null
  role?: string
}

interface OrgMember {
  id: string
  email: string | null
  full_name: string | null
  role: string
  joined_at: string
}

interface OrgInvite {
  id: string
  email: string
  role: string
  expires_at: string
  created_at: string
  invited_by_name?: string
  invite_url: string
  email_sent?: boolean
}

interface DeletePreview {
  name: string
  document_count: number
  member_count: number
}

interface MyPendingInvite {
  id: string
  organization_id: string
  organization_name: string
  role: string
  expires_at: string
  created_at: string
  invite_url: string
  thread_id?: string | null
}

type GoogleAccount = {
  id: string
  email: string | null
  label: string | null
  connectedAt: string
}

/** Format an ISO timestamp as a short relative age (e.g. "3h ago"). */
function formatRelativeDate(iso?: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const ORG_ICONS = WORKSPACE_ICONS

const ORG_TYPES: { value: OrgType; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'client', label: 'Client' },
  { value: 'community', label: 'Community' },
  { value: 'other', label: 'Other' },
]

function hexToRgba(hex: string, alpha: number): string {
  const normalized = normalizeWorkspaceHex(hex)
  if (!normalized) return `rgba(100, 116, 139, ${alpha})`
  const r = parseInt(normalized.slice(1, 3), 16)
  const g = parseInt(normalized.slice(3, 5), 16)
  const b = parseInt(normalized.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function OrgIndicator({
  org,
  className = 'h-4 w-4 shrink-0',
}: {
  org: { icon?: string | null; color?: string | null; type?: string; show_in_sidebar?: boolean }
  className?: string
}) {
  const Icon = getWorkspaceIcon(org.icon, org.type === 'personal' ? User : Building2)
  const hasColor = !!org.color
  const enabled = org.show_in_sidebar !== false
  return (
    <div className={`relative w-8 h-6 shrink-0 flex items-center justify-center ${className}`}>
      {hasColor ? (
        <div
          className={`absolute inset-0 rounded-md border border-border transition-opacity ${!enabled ? 'opacity-20' : ''}`}
          style={{ backgroundColor: hexToRgba(org.color!, 0.5) }}
        />
      ) : (
        <div
          className={`absolute inset-0 rounded-md bg-muted transition-opacity ${!enabled ? 'opacity-20' : ''}`}
        />
      )}
      <Icon
        className={`relative z-10 h-5 w-5 ${hasColor ? 'text-white drop-shadow-sm' : 'text-foreground'}`}
      />
    </div>
  )
}

interface OrganizationSettingsProps {
  onSaved?: () => void
  /** Drill into this workspace when settings opens from a sidebar deep link */
  focusOrganizationId?: string | null
  /** e.g. basecamp / basecamp-listening - scroll to that workspace extensions panel */
  focusSection?: string | null
  /** Clear parent deep-link after it has been applied (allows search re-clicks). */
  onFocusSectionConsumed?: () => void
  /** Clear parent org focus when user returns to the Workspaces list via breadcrumb. */
  onLeaveWorkspaceList?: () => void
}

export function OrganizationSettings({
  onSaved,
  focusOrganizationId,
  focusSection,
  onFocusSectionConsumed,
  onLeaveWorkspaceList,
}: OrganizationSettingsProps) {
  const { refetchWorkspaces } = useWorkspace()
  const [orgs, setOrgs] = useState<ApiOrg[]>([])
  const [loading, setLoading] = useState(true)
  const [filesystemEnabled, setFilesystemEnabled] = useState(true)
  const [settingsOrgId, setSettingsOrgId] = useState<string | null>(focusOrganizationId ?? null)
  const [workspaceSection, setWorkspaceSection] = useState<WorkspaceSectionId>(
    () => parseFocusSection(focusSection).section ?? 'overview',
  )
  const [extensionId, setExtensionId] = useState<string | null>(null)
  const [newOrgName, setNewOrgName] = useState('')
  const [newOrgSlug, setNewOrgSlug] = useState('')
  const [newOrgType, setNewOrgType] = useState<OrgType>('work')
  const [newOrgIcon, setNewOrgIcon] = useState('Building2')
  const [newOrgColor, setNewOrgColor] = useState(DEFAULT_WORKSPACE_COLOR)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<OrgMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [invites, setInvites] = useState<OrgInvite[]>([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'member' | 'admin' | 'viewer'>('member')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [extensionToggling, setExtensionToggling] = useState<string | null>(null)
  const [basecampDisconnecting, setBasecampDisconnecting] = useState(false)
  const [basecampJustDisconnected, setBasecampJustDisconnected] = useState(false)
  const [googleDriveConnecting, setGoogleDriveConnecting] = useState(false)
  const [googleAccounts, setGoogleAccounts] = useState<GoogleAccount[]>([])
  const [loadingGoogleAccounts, setLoadingGoogleAccounts] = useState(false)
  const [disconnectingGoogleAccountId, setDisconnectingGoogleAccountId] = useState<string | null>(null)
  const [workspaceGoogleTokenId, setWorkspaceGoogleTokenId] = useState<string | null>(null)
  const [assigningGoogleAccount, setAssigningGoogleAccount] = useState(false)
  const [bcAccounts, setBcAccounts] = useState<{ id: number; name: string; href: string }[]>([])
  const [bcAccountsLoading, setBcAccountsLoading] = useState(false)
  const [bcAccountSaving, setBcAccountSaving] = useState(false)
  const [myPendingInvites, setMyPendingInvites] = useState<MyPendingInvite[]>([])
  const [myPendingInvitesLoading, setMyPendingInvitesLoading] = useState(false)
  const [deletePreview, setDeletePreview] = useState<DeletePreview | null>(null)
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false)
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editOrgName, setEditOrgName] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)

  // Cursor PAT state
  const [cursorTokens, setCursorTokens] = useState<{ id: string; name: string; created_at: string | null; last_used_at: string | null; expires_at: string | null; scopes: string[] }[]>([])
  const [cursorTokensLoading, setCursorTokensLoading] = useState(false)
  const [cursorTokenName, setCursorTokenName] = useState('Cursor on my Mac')
  const [cursorGenerating, setCursorGenerating] = useState(false)
  const [cursorNewToken, setCursorNewToken] = useState<string | null>(null)
  const [cursorRevokingId, setCursorRevokingId] = useState<string | null>(null)
  const [cursorVsixFilename, setCursorVsixFilename] = useState<string | null>(null)

  const currentOrg = settingsOrgId && settingsOrgId !== 'new' ? orgs.find((o) => o.id === settingsOrgId) : null

  const canManageMembers = currentOrg && (currentOrg.role === 'admin' || currentOrg.role === 'owner')

  useEffect(() => {
    if (!currentOrg) {
      setEditOrgName('')
      setRenameError(null)
      return
    }
    setEditOrgName(currentOrg.name)
    setRenameError(null)
  }, [currentOrg?.id, currentOrg?.name])

  const { extensions, isLoading: extensionsLoading, toggleExtension, updateExtensionConfig } = useWorkspaceExtensions(currentOrg?.id)
  const { authStatus: basecampAuth, refetch: refetchBasecampAuth } = useBasecampAuth(currentOrg?.id)
  const { refetch: refetchGoogleDriveAuth } = useGoogleDriveAuth(currentOrg?.id)
  const basecampRow = extensions.find((e) => e.extension_id === 'basecamp')
  const googleDriveRow = extensions.find((e) => e.extension_id === 'google-drive')
  const zoomRow = extensions.find((e) => e.extension_id === 'zoom')
  const smtpRow = extensions.find((e) => e.extension_id === 'smtp')
  const uptimeRow = extensions.find((e) => e.extension_id === 'uptime')
  const nextcloudRow = extensions.find((e) => e.extension_id === 'nextcloud')
  const slackRow = extensions.find((e) => e.extension_id === 'slack')
  const cursorRow = extensions.find((e) => e.extension_id === 'cursor')

  const loadCursorExtensionInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/extensions/cursor/info`)
      if (res.ok) {
        const data = await res.json()
        setCursorVsixFilename(typeof data.filename === 'string' ? data.filename : null)
      }
    } catch (e) {
      console.error('Failed to load cursor extension info:', e)
    }
  }

  const loadCursorTokens = async () => {
    setCursorTokensLoading(true)
    try {
      const res = await fetchWithAuth('/api/settings/cursor/tokens')
      if (res.ok) {
        const data = await res.json()
        setCursorTokens(data)
      }
    } catch (e) {
      console.error('Failed to load cursor tokens:', e)
    } finally {
      setCursorTokensLoading(false)
    }
  }

  const generateCursorToken = async () => {
    setCursorGenerating(true)
    setCursorNewToken(null)
    try {
      const res = await fetchWithAuth('/api/settings/cursor/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cursorTokenName }),
      })
      if (res.ok) {
        const data = await res.json()
        setCursorNewToken(data.token)
        await loadCursorTokens()
        toast.success('Token created — copy it now, it won\'t be shown again')
      } else {
        toast.error('Failed to generate token')
      }
    } catch (e) {
      toast.error('Failed to generate token')
    } finally {
      setCursorGenerating(false)
    }
  }

  const revokeCursorToken = async (tokenId: string) => {
    setCursorRevokingId(tokenId)
    try {
      const res = await fetchWithAuth(`/api/settings/cursor/tokens/${tokenId}`, { method: 'DELETE' })
      if (res.ok) {
        setCursorTokens((prev) => prev.filter((t) => t.id !== tokenId))
        toast.success('Token revoked')
      } else {
        toast.error('Failed to revoke token')
      }
    } catch (e) {
      toast.error('Failed to revoke token')
    } finally {
      setCursorRevokingId(null)
    }
  }

  const loadOrgs = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth('/api/organizations')
      if (res.ok) {
        const data = await res.json()
        setOrgs(Array.isArray(data) ? data : [])
      }
    } catch (e) {
      console.error('Failed to load organizations:', e)
      setOrgs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (settingsOrgId !== null) return
    let cancelled = false
    setMyPendingInvitesLoading(true)
    fetchWithAuth('/api/account/workspace-invites')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setMyPendingInvites(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setMyPendingInvites([])
      })
      .finally(() => {
        if (!cancelled) setMyPendingInvitesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [settingsOrgId])

  useEffect(() => {
    loadOrgs()
    fetchWithAuth('/api/settings')
      .then((r) => r.json())
      .then((data) => {
        if (data?.settings?.features?.filesystemEnabled === false) {
          setFilesystemEnabled(false)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!focusOrganizationId) return
    setSettingsOrgId(focusOrganizationId)
  }, [focusOrganizationId])

  useEffect(() => {
    if (!focusOrganizationId || loading) return
    if (!orgs.some((o) => o.id === focusOrganizationId)) {
      setSettingsOrgId(null)
    }
  }, [focusOrganizationId, loading, orgs])

  // Deep-link: map focusSection → workspace section / extension page
  useEffect(() => {
    if (!focusSection || loading || !settingsOrgId || settingsOrgId === 'new') return
    const parsed = parseFocusSection(focusSection)
    if (parsed.section) setWorkspaceSection(parsed.section)
    if (parsed.extensionId) setExtensionId(parsed.extensionId)
    else if (parsed.section && parsed.section !== 'extensions') setExtensionId(null)

    // Drop parent residue so the same search hit can re-open after breadcrumb back.
    onFocusSectionConsumed?.()

    const timer = window.setTimeout(() => {
      if (focusSection === 'basecamp-listening' || focusSection === 'listening') {
        const listening = document.querySelector(
          `[data-testid="${testIds.workspaceSettings.basecampListeningPanel}"]`
        )
        if (listening instanceof HTMLElement) {
          listening.scrollIntoView({ behavior: 'smooth', block: 'start' })
          listening.classList.add('ring-2', 'ring-primary/40')
          window.setTimeout(() => listening.classList.remove('ring-2', 'ring-primary/40'), 2400)
        }
      }
    }, 160)
    return () => window.clearTimeout(timer)
  }, [focusSection, loading, settingsOrgId, onFocusSectionConsumed])

  useEffect(() => {
    if (basecampAuth?.authenticated) {
      setBasecampJustDisconnected(false)
    }
  }, [basecampAuth?.authenticated])

  /** Load connected Google Drive accounts for the settings accounts list. */
  const fetchGoogleAccounts = async () => {
    setLoadingGoogleAccounts(true)
    try {
      const orgParam = currentOrg?.id ? `?organizationId=${encodeURIComponent(currentOrg.id)}` : ''
      const res = await fetchWithAuth(`/api/google-drive/accounts${orgParam}`)
      if (res.ok) {
        const data = (await res.json()) as { accounts?: GoogleAccount[]; workspaceTokenId?: string | null }
        setGoogleAccounts(data.accounts ?? [])
        setWorkspaceGoogleTokenId(data.workspaceTokenId ?? null)
      }
    } catch (e) {
      console.error('Failed to load Google accounts:', e)
    } finally {
      setLoadingGoogleAccounts(false)
    }
  }

  useEffect(() => {
    if (extensionId !== 'google-drive' && !googleDriveRow?.enabled) return
    void fetchGoogleAccounts()
  }, [extensionId, googleDriveRow?.enabled])

  useEffect(() => {
    if (extensionId !== 'google-drive' && !googleDriveRow?.enabled) return
    const onAuthSuccess = () => {
      void fetchGoogleAccounts()
      void refetchGoogleDriveAuth()
    }
    window.addEventListener('google-drive-auth-success', onAuthSuccess)
    return () => window.removeEventListener('google-drive-auth-success', onAuthSuccess)
  }, [extensionId, googleDriveRow?.enabled, refetchGoogleDriveAuth])

  useEffect(() => {
    if (extensionId === 'cursor' || cursorRow?.enabled) {
      loadCursorExtensionInfo()
      loadCursorTokens()
    }
  }, [extensionId, cursorRow?.enabled])

  useEffect(() => {
    if (!currentOrg?.id) {
      setMembers([])
      setInvites([])
      return
    }
    let cancelled = false
    setMembersLoading(true)
    fetchWithAuth(`/api/organizations/${currentOrg.id}/members`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setMembers(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setMembers([])
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentOrg?.id])

  useEffect(() => {
    if (!currentOrg?.id) {
      setInvites([])
      return
    }
    const canManage = currentOrg.role === 'admin' || currentOrg.role === 'owner'
    if (!canManage) {
      setInvites([])
      return
    }
    let cancelled = false
    setInvitesLoading(true)
    fetchWithAuth(`/api/organizations/${currentOrg.id}/invites`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setInvites(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setInvites([])
      })
      .finally(() => {
        if (!cancelled) setInvitesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentOrg?.id, currentOrg?.role])

  useEffect(() => {
    if (!currentOrg?.id || !canManageMembers) {
      setDeletePreview(null)
      setDeleteConfirmName('')
      return
    }
    let cancelled = false
    setDeletePreviewLoading(true)
    fetchWithAuth(`/api/organizations/${currentOrg.id}/delete-preview`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setDeletePreview(data as DeletePreview)
      })
      .catch(() => {
        if (!cancelled) setDeletePreview(null)
      })
      .finally(() => {
        if (!cancelled) setDeletePreviewLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentOrg?.id, canManageMembers])

  const updateOrgField = async (orgId: string, field: string, value: string) => {
    const payloadValue =
      field === 'color'
        ? (normalizeWorkspaceHex(value) ?? value)
        : value
    if (field === 'color' && !normalizeWorkspaceHex(value)) {
      toast.error('Invalid color - use a hex like #c45a11')
      return
    }
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: payloadValue }),
      })
      if (res.ok) {
        await loadOrgs()
        await refetchWorkspaces()
      } else {
        const data = await res.json().catch(() => ({} as { error?: string }))
        toast.error(typeof data?.error === 'string' ? data.error : `Update failed (${res.status})`)
      }
    } catch (e) {
      console.error('Failed to update organization:', e)
      toast.error('Failed to update workspace')
    }
  }

  const ORG_NAME_MAX_LEN = 80

  const handleRenameWorkspace = async () => {
    if (!currentOrg?.id || !canManageMembers) return
    const trimmed = editOrgName.trim()
    if (!trimmed) {
      setRenameError('Name is required')
      return
    }
    if (trimmed.length > ORG_NAME_MAX_LEN) {
      setRenameError(`Name must be ${ORG_NAME_MAX_LEN} characters or fewer`)
      return
    }
    if (trimmed === currentOrg.name) {
      setRenameError(null)
      return
    }
    setRenaming(true)
    setRenameError(null)
    try {
      const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed }),
      })
      if (res.ok) {
        toast.success('Workspace renamed')
        await loadOrgs()
        await refetchWorkspaces()
        onSaved?.()
      } else {
        const data = await res.json().catch(() => ({} as { error?: string }))
        const msg = typeof data?.error === 'string' ? data.error : `Rename failed (${res.status})`
        setRenameError(msg)
        toast.error(msg)
      }
    } catch (e) {
      console.error('Failed to rename workspace:', e)
      setRenameError('Failed to rename workspace')
      toast.error('Failed to rename workspace')
    } finally {
      setRenaming(false)
    }
  }

  const handleDeleteWorkspace = async () => {
    if (!currentOrg?.id || !canManageMembers) return
    const needsName = (deletePreview?.document_count ?? 0) > 0
    if (needsName && deleteConfirmName.trim() !== currentOrg.name) return

    setDeleting(true)
    try {
      const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          needsName ? { confirmName: deleteConfirmName.trim() } : {}
        ),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({} as { error?: string }))
        toast.error(typeof data?.error === 'string' ? data.error : 'Failed to delete workspace')
        return
      }
      toast.success('Workspace deleted')
      setDeleteDialogOpen(false)
      setDeleteConfirmName('')
      setSettingsOrgId(null)
      await loadOrgs()
      await refetchWorkspaces()
      onSaved?.()
    } catch {
      toast.error('Failed to delete workspace')
    } finally {
      setDeleting(false)
    }
  }

  const handleInvite = async () => {
    if (!currentOrg?.id || !inviteEmail.trim()) return
    setInviting(true)
    setInviteError(null)
    try {
      const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      })
      const data = await res.json()
      if (!res.ok) {
        setInviteError(data.error || 'Failed to create invite')
        return
      }
      setInviteEmail('')
      setInvites((prev) => [{ ...data, invite_url: data.invite_url }, ...prev])
      if (data.email_sent) {
        toast.success('Invite email sent')
      } else {
        toast.success('Invite created. Copy the link to share (email not sent).')
      }
      onSaved?.()
    } catch {
      setInviteError('Failed to create invite')
    } finally {
      setInviting(false)
    }
  }

  const handleCancelInvite = async (inviteId: string) => {
    if (!currentOrg?.id) return
    const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}/invites/${inviteId}`, {
      method: 'DELETE',
    })
    if (res.ok) {
      setInvites((prev) => prev.filter((i) => i.id !== inviteId))
      onSaved?.()
    }
  }

  const handleCopyInviteLink = (url: string) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success('Invite link copied'),
      () => toast.error('Failed to copy')
    )
  }

  const handleOpenPendingInvite = (inv: MyPendingInvite) => {
    if (inv.thread_id) {
      openInboxThread(inv.thread_id, `Workspace invite: ${inv.organization_name}`)
      return
    }
    if (inv.invite_url) {
      window.location.assign(inv.invite_url)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!currentOrg?.id) return
    const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}/members/${memberId}`, {
      method: 'DELETE',
    })
    if (res.ok) {
      setMembers((prev) => prev.filter((m) => m.id !== memberId))
      onSaved?.()
    }
  }

  const handleUpdateMemberRole = async (memberId: string, role: string) => {
    if (!currentOrg?.id) return
    const res = await fetchWithAuth(`/api/organizations/${currentOrg.id}/members/${memberId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    })
    if (res.ok) {
      const data = await res.json()
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, ...data } : m)))
      onSaved?.()
    }
  }

  const prepareCreateForm = () => {
    setNewOrgName('')
    setNewOrgSlug('')
    setNewOrgType('work')
    setNewOrgIcon('Building2')
    setNewOrgColor(DEFAULT_WORKSPACE_COLOR)
    setError(null)
  }

  const handleCreate = async () => {
    if (!newOrgName.trim()) return
    setCreating(true)
    setError(null)
    try {
      const res = await fetchWithAuth('/api/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newOrgName.trim(),
          type: newOrgType,
          icon: newOrgIcon,
          color: normalizeWorkspaceHex(newOrgColor) ?? DEFAULT_WORKSPACE_COLOR,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
                  setError(data.error || 'Failed to create workspace')
                return
      }
      prepareCreateForm()
      setSettingsOrgId(null)
      await loadOrgs()
      await refetchWorkspaces()
    } catch (e) {
      setError('Failed to create workspace')
    } finally {
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const orgName =
    settingsOrgId === 'new' ? 'New workspace' : currentOrg?.name ?? 'Workspace'

  const leaveWorkspace = () => {
    setSettingsOrgId(null)
    setExtensionId(null)
    setWorkspaceSection('overview')
    setError(null)
    onLeaveWorkspaceList?.()
  }

  const leaveExtension = () => {
    setExtensionId(null)
    setWorkspaceSection('extensions')
    onFocusSectionConsumed?.()
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky breadcrumbs (outside scroll body) */}
      {settingsOrgId != null && (
        <div
          className="breadcrumb-nav shrink-0 bg-muted/60"
          data-testid={testIds.workspaceSettings.breadcrumbs}
        >
          <button
            type="button"
            className="breadcrumb-nav__link"
            onClick={leaveWorkspace}
          >
            Workspaces
          </button>
          <ChevronRight className="h-4 w-4 shrink-0" />
          {extensionId ? (
            <>
              <button
                type="button"
                className="breadcrumb-nav__link"
                onClick={leaveExtension}
              >
                {orgName}
              </button>
              <ChevronRight className="h-4 w-4 shrink-0" />
              <span className="breadcrumb-nav__current">
                {EXTENSION_LABELS[extensionId] ?? extensionId}
              </span>
            </>
          ) : (
            <span className="breadcrumb-nav__current">{orgName}</span>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
      {/* List view */}
      {settingsOrgId === null && (
        <TooltipProvider>
          <div className="space-y-4 p-6">
          <div>
            <h3 className="text-lg font-semibold mb-1">Workspaces</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create workspaces, manage memberships, accept invites.
            </p>
            <h4 className="text-sm font-medium mb-2">My workspaces</h4>
            <div className="space-y-2">
              {orgs.map((org) => (
                <div
                  key={org.id}
                  className={`flex items-center justify-between p-3 rounded-lg bg-muted/30 ${org.color ? 'border-l-4' : ''}`}
                  style={org.color ? { borderLeftColor: org.color } : undefined}
                >
                  <div className="flex items-center gap-3">
                    <OrgIndicator org={org} />
                    <div>
                      <div className="font-medium">
                        {org.name}
                        {org.type === 'personal' && ' (you)'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {org.type === 'personal'
                          ? 'Your private notes'
                          : `${ORG_TYPES.find((t) => t.value === org.type)?.label ?? org.type} · ${org.role ?? '—'}`}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Org settings"
                    onClick={() => {
                      setSettingsOrgId(org.id)
                      setWorkspaceSection('overview')
                      setExtensionId(null)
                    }}
                  >
                    <SettingsIcon className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <Button
            className="w-full"
            variant="outline"
            onClick={() => {
              prepareCreateForm()
              setSettingsOrgId('new')
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create workspace
          </Button>
          <div data-testid={testIds.workspaceSettings.pendingInvitesSection}>
            <h4 className="text-sm font-medium mb-2">Invited / Pending</h4>
            {myPendingInvitesLoading ? (
              <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Loading invites…
              </div>
            ) : myPendingInvites.length === 0 ? (
              <div className="p-3 rounded-lg bg-muted/30 text-sm text-muted-foreground">
                No pending invites
              </div>
            ) : (
              <div className="space-y-2">
                {myPendingInvites.map((inv) => (
                  <div
                    key={inv.id}
                    data-testid={testIds.workspaceSettings.pendingInviteRow(inv.id)}
                    className="p-3 rounded-lg bg-muted/30 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{inv.organization_name}</div>
                      <div className="text-xs text-muted-foreground">
                        Role: {inv.role}
                        {' · '}
                        Expires{' '}
                        {inv.expires_at
                          ? new Date(inv.expires_at).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid={testIds.workspaceSettings.pendingInviteOpenLink(inv.id)}
                        onClick={() => handleOpenPendingInvite(inv)}
                      >
                        <Inbox className="h-3.5 w-3.5 mr-1.5" />
                        Open invite
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid={testIds.workspaceSettings.pendingInviteCopyLink(inv.id)}
                            onClick={() => handleCopyInviteLink(inv.invite_url)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Copy invite link</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </TooltipProvider>
      )}

      {/* Create view */}
      {settingsOrgId === 'new' && (
        <div className="space-y-6 max-w-2xl p-6">
          <div>
            <h3 className="text-lg font-semibold mb-1">New workspace</h3>
            <p className="text-sm text-muted-foreground">Add a new shared workspace.</p>
          </div>
          <div className="space-y-4 rounded-lg bg-muted/30 p-4">
            <div>
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                placeholder="Acme Corp"
                className="mt-2"
                value={newOrgName}
                onChange={(e) => {
                  const name = e.target.value
                  setNewOrgName(name)
                  setNewOrgSlug(name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''))
                }}
              />
            </div>
            <div>
              <Label htmlFor="org-slug">Slug (optional)</Label>
              <Input
                id="org-slug"
                placeholder="acme-corp"
                className="mt-2"
                value={newOrgSlug}
                onChange={(e) => setNewOrgSlug(e.target.value)}
              />
            </div>
            <div>
              <Label>Type</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ORG_TYPES.filter((t) => t.value !== 'personal').map(({ value, label }) => (
                  <Button
                    key={value}
                    type="button"
                    variant={newOrgType === value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewOrgType(value)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ORG_ICONS.map(({ value, Icon, label }) => (
                  <Button
                    key={value}
                    type="button"
                    variant={newOrgIcon === value ? 'default' : 'outline'}
                    size="icon"
                    title={label}
                    onClick={() => setNewOrgIcon(value)}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="mt-2">
                <WorkspaceColorPicker
                  id="new-workspace-color"
                  value={newOrgColor}
                  onChange={setNewOrgColor}
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsOrgId(null)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!newOrgName.trim() || creating}>
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Per-org edit view */}
      {settingsOrgId != null && settingsOrgId !== 'new' && currentOrg && (
        <div className="max-w-3xl p-6">
          {!extensionId ? (
          <div className="flex gap-6">
            <nav
              className="w-40 shrink-0 space-y-0.5 sticky top-0 self-start"
              data-testid={testIds.workspaceSettings.subNav}
            >
              {WORKSPACE_SECTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setWorkspaceSection(s.id)}
                  className={cn(
                    'w-full min-h-9 text-left text-[13px] font-medium px-2 py-2 rounded-md transition-colors',
                    workspaceSection === s.id
                      ? 'bg-accent font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-accent/50',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </nav>
            <div className="flex-1 min-w-0 space-y-6">
          {workspaceSection === 'overview' && (
          <div data-testid={testIds.workspaceSettings.sectionOverview} className="space-y-6">
          <div className="flex items-center gap-3">
            <OrgIndicator org={currentOrg} />
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold truncate">{currentOrg.name}</h3>
              <p className="text-sm text-muted-foreground">
                {currentOrg.type === 'personal'
                  ? 'Your private notes'
                  : `${ORG_TYPES.find((t) => t.value === currentOrg.type)?.label ?? currentOrg.type} · ${currentOrg.role ?? '—'}`}
              </p>
            </div>
          </div>
          <div className="space-y-3 rounded-lg bg-muted/30 p-4">
            <div>
              <Label htmlFor="workspace-rename">Workspace name</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Display label in the sidebar, New menu, and settings. Storage path stays the same.
              </p>
              {canManageMembers ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                  <Input
                    id="workspace-rename"
                    data-testid={testIds.workspaceSettings.renameInput}
                    value={editOrgName}
                    maxLength={ORG_NAME_MAX_LEN}
                    disabled={renaming}
                    onChange={(e) => {
                      setEditOrgName(e.target.value)
                      if (renameError) setRenameError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        void handleRenameWorkspace()
                      }
                    }}
                    className="sm:max-w-md"
                  />
                  <Button
                    type="button"
                    data-testid={testIds.workspaceSettings.renameSave}
                    disabled={
                      renaming ||
                      !editOrgName.trim() ||
                      editOrgName.trim() === currentOrg.name
                    }
                    onClick={() => void handleRenameWorkspace()}
                  >
                    {renaming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save name'}
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid={testIds.workspaceSettings.renameReadonly}>
                  Only workspace admins can rename this workspace.
                </p>
              )}
              {renameError && (
                <p className="text-sm text-destructive mt-2" role="alert">
                  {renameError}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-4 rounded-lg bg-muted/30 p-4">
            <h4 className="text-sm font-medium">Icon & color</h4>
            {currentOrg.type !== 'personal' && (
              <div>
                <Label>Type</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ORG_TYPES.filter((t) => t.value !== 'personal').map(({ value, label }) => (
                    <Button
                      key={value}
                      type="button"
                      variant={currentOrg.type === value ? 'default' : 'outline'}
                      size="sm"
                      disabled={!canManageMembers}
                      onClick={() => updateOrgField(currentOrg.id, 'type', value)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ORG_ICONS.map(({ value, Icon, label }) => (
                  <Button
                    key={value}
                    type="button"
                    variant={(currentOrg.icon ?? 'Building2') === value ? 'default' : 'outline'}
                    size="icon"
                    title={label}
                    disabled={!canManageMembers}
                    onClick={() => updateOrgField(currentOrg.id, 'icon', value)}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="mt-2">
                <WorkspaceColorPicker
                  id={`workspace-color-${currentOrg.id}`}
                  value={currentOrg.color ?? DEFAULT_WORKSPACE_COLOR}
                  disabled={!canManageMembers}
                  onChange={(hex) => updateOrgField(currentOrg.id, 'color', hex)}
                />
              </div>
            </div>
            {!canManageMembers && (
              <p className="text-xs text-muted-foreground">
                Only workspace admins can change name, icon, color, and type.
              </p>
            )}
          </div>
          {filesystemEnabled && (
            <div className="space-y-4 rounded-lg bg-muted/30 p-4">
              <div>
                <h4 className="text-sm font-medium mb-1">Content path</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Stable storage prefix for this workspace (assigned automatically at creation).
                  Used by folder creation, Move Folder, and workspace filtering.
                </p>
                <p className="text-xs font-mono text-muted-foreground">
                  {(currentOrg.content_root && currentOrg.content_root.trim())
                    ? currentOrg.content_root
                    : 'Not assigned yet — run migration backfill or recreate the workspace.'}
                </p>
              </div>
            </div>
          )}
          </div>
          )}

          {/* Workspace Settings: Extensions, Members, Your Integrations */}
          <TooltipProvider>
            <div className="space-y-4">
              {/* Extensions */}
              {workspaceSection === 'extensions' && (
              <SettingsSection
                data-testid={testIds.workspaceSettings.sectionExtensions}
                title="Extensions"
                description="Enable limbs for this workspace. Use Configure for setup and instructions (Basecamp, Google Drive, Cursor, Zoom)."
              >
                <div className="space-y-4">
                  {extensionsLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading extensions…
                    </div>
                  ) : extensions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No extensions returned for this workspace. Check that you are an org member and the API is reachable.
                    </p>
                  ) : (
                    extensions.map((ext) => {
                      const label = EXTENSION_LABELS[ext.extension_id] ?? ext.extension_id
                      const description = EXTENSION_DESCRIPTIONS[ext.extension_id] ?? ''
                      const canToggle = currentOrg.role === 'admin' || currentOrg.role === 'owner'
                      const isToggling = extensionToggling === ext.extension_id
                      const hasSetup = (EXTENSIONS_WITH_SETUP as readonly string[]).includes(
                        ext.extension_id,
                      )

                      const switchEl = (
                        <Switch
                          checked={ext.enabled}
                          onCheckedChange={async (checked) => {
                            if (!canToggle) return
                            setExtensionToggling(ext.extension_id)
                            try {
                              await toggleExtension(ext.extension_id, checked)
                              onSaved?.()
                            } catch (e) {
                              console.error('Failed to toggle extension:', e)
                            } finally {
                              setExtensionToggling(null)
                            }
                          }}
                          disabled={!canToggle || isToggling}
                        />
                      )

                      return (
                        <div
                          key={ext.extension_id}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{label}</p>
                            {description && (
                              <p className="text-xs text-muted-foreground">{description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {ext.enabled && hasSetup ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs"
                                data-testid={testIds.workspaceSettings.configureExtension(
                                  ext.extension_id,
                                )}
                                onClick={() => setExtensionId(ext.extension_id)}
                              >
                                Configure
                                <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
                              </Button>
                            ) : null}
                            {canToggle ? (
                              isToggling ? (
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                switchEl
                              )
                            ) : (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">{switchEl}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Only admins can enable or disable extensions
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </SettingsSection>
              )}

              {/* Members */}
              {workspaceSection === 'team' && (
              <SettingsSection
                data-testid={testIds.workspaceSettings.sectionTeam}
                title="Members"
                description={
                  membersLoading
                    ? 'Loading…'
                    : `${members.length} member${members.length !== 1 ? 's' : ''}`
                }
              >
                <div className="space-y-4">
                  {membersLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading members…
                    </div>
                  ) : (
                    <>
                      {members.map((m) => (
                        <div
                          key={m.id}
                          className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                        >
                          <div>
                            <p className="text-sm">{m.full_name || m.email || 'Unknown'}</p>
                            {m.full_name && m.email && (
                              <p className="text-xs text-muted-foreground">{m.email}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {canManageMembers && m.role !== 'owner' ? (
                              <>
                                <Select
                                  value={m.role}
                                  onValueChange={(role) => handleUpdateMemberRole(m.id, role)}
                                >
                                  <SelectTrigger className="w-24 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="admin">admin</SelectItem>
                                    <SelectItem value="member">member</SelectItem>
                                    <SelectItem value="viewer">viewer</SelectItem>
                                  </SelectContent>
                                </Select>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}>
                                    <DropdownMenuItem
                                      className={QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
                                      onClick={() => handleRemoveMember(m.id)}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            ) : (
                              <Badge variant="outline">{m.role}</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {canManageMembers && (
                        <div className="pt-4 space-y-3 border-t border-border/50">
                          <h5 className="text-sm font-medium">Invite member</h5>
                          <div className="flex gap-2 flex-wrap">
                            <Input
                              placeholder="email@example.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              className="flex-1 min-w-[180px]"
                              data-testid={testIds.workspaceSettings.inviteEmailInput}
                            />
                            <Select value={inviteRole} onValueChange={(v: 'member' | 'admin' | 'viewer') => setInviteRole(v)}>
                              <SelectTrigger className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">member</SelectItem>
                                <SelectItem value="admin">admin</SelectItem>
                                <SelectItem value="viewer">viewer</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={handleInvite}
                              disabled={!inviteEmail.trim() || inviting}
                              data-testid={testIds.workspaceSettings.inviteSubmitButton}
                            >
                              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1" />}
                              Invite
                            </Button>
                          </div>
                          {inviteError && <p className="text-xs text-destructive">{inviteError}</p>}
                          {invitesLoading ? (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading invites…
                            </div>
                          ) : invites.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground">Pending invites</p>
                              {invites.map((inv) => (
                                <div
                                  key={inv.id}
                                  className="flex items-center justify-between py-2 px-2 rounded bg-muted/50 text-sm"
                                >
                                  <div>
                                    <span className="font-medium">{inv.email}</span>
                                    <span className="text-muted-foreground ml-2">({inv.role})</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7"
                                          onClick={() => handleCopyInviteLink(inv.invite_url)}
                                        >
                                          <Copy className="h-3.5 w-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Copy invite link</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7"
                                          onClick={() => handleCancelInvite(inv.id)}
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Revoke invite</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </SettingsSection>
              )}

              {workspaceSection === 'keys' && currentOrg && currentOrg.type !== 'personal' && (
                <div data-testid={testIds.workspaceSettings.sectionKeys}>
                  <WorkspaceSecrets
                    orgId={currentOrg.id}
                    isAdmin={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  />
                </div>
              )}
              {workspaceSection === 'usage' && currentOrg && (
                <WorkspaceUsage
                  orgId={currentOrg.id}
                  isAdmin={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                />
              )}
              {workspaceSection === 'keys' && currentOrg?.type === 'personal' && (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid={testIds.workspaceSettings.sectionKeys}
                >
                  Personal workspaces use account-level keys. Switch to a Work workspace for
                  shared LLM provider secrets.
                </p>
              )}

              {workspaceSection === 'danger' && canManageMembers && currentOrg && (
                <SettingsSection
                  variant="danger"
                  data-testid={testIds.workspaceSettings.deleteSection}
                  title={<span className="text-destructive">Delete workspace</span>}
                  description={
                    <>
                      Permanently delete this workspace and all of its data. This cannot be undone.
                      {deletePreviewLoading ? (
                        ' Loading impact…'
                      ) : deletePreview ? (
                        ` ${deletePreview.document_count} document${deletePreview.document_count === 1 ? '' : 's'}, ${deletePreview.member_count} member${deletePreview.member_count === 1 ? '' : 's'}.`
                      ) : null}
                    </>
                  }
                >
                    <AlertDialog
                      open={deleteDialogOpen}
                      onOpenChange={(open) => {
                        setDeleteDialogOpen(open)
                        if (!open) setDeleteConfirmName('')
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          data-testid={testIds.workspaceSettings.deleteTrigger}
                        >
                          Delete workspace
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent data-testid={testIds.workspaceSettings.deleteDialog}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete &ldquo;{currentOrg.name}&rdquo;?</AlertDialogTitle>
                          <AlertDialogDescription asChild>
                            <div className="space-y-3 text-sm text-muted-foreground">
                              <p>
                                This permanently removes the workspace, members, invites, documents,
                                and related settings. Files on disk under the content path are not
                                deleted automatically.
                              </p>
                              {(deletePreview?.document_count ?? 0) > 0 && (
                                <div className="space-y-2">
                                  <p>
                                    This workspace has{' '}
                                    <strong>{deletePreview?.document_count}</strong> document
                                    {(deletePreview?.document_count ?? 0) === 1 ? '' : 's'}.
                                    Type the workspace name to confirm:
                                  </p>
                                  <Input
                                    value={deleteConfirmName}
                                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                                    placeholder={currentOrg.name}
                                    data-testid={testIds.workspaceSettings.deleteConfirmInput}
                                    autoComplete="off"
                                  />
                                </div>
                              )}
                            </div>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={
                              deleting ||
                              ((deletePreview?.document_count ?? 0) > 0 &&
                                deleteConfirmName.trim() !== currentOrg.name)
                            }
                            data-testid={testIds.workspaceSettings.deleteConfirmButton}
                            onClick={(e) => {
                              e.preventDefault()
                              void handleDeleteWorkspace()
                            }}
                          >
                            {deleting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              'Delete permanently'
                            )}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                </SettingsSection>
              )}
            </div>
          </TooltipProvider>
            </div>
          </div>
          ) : (
          <TooltipProvider>
            <div className="space-y-4">
              {/* Basecamp extension page */}
              {extensionId === 'basecamp' && (
                <ExtensionSettingsShell
                  title="Basecamp"
                  description="Reach into Basecamp from this workspace. Todos stay in Basecamp; Maater links work specs and pulls on demand."
                  enabled={Boolean(basecampRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'basecamp'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('basecamp')
                    try {
                      await toggleExtension('basecamp', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  statusLabel={
                    basecampJustDisconnected || !basecampAuth?.authenticated
                      ? 'Disabled'
                      : 'Enabled'
                  }
                  data-testid={testIds.workspaceSettings.extensionPage('basecamp')}
                >
                <div
                  className="space-y-6"
                  data-testid={testIds.workspaceSettings.basecampPanel}
                >
                  <p className="text-xs text-muted-foreground leading-relaxed rounded-md border border-border/50 bg-muted/10 px-4 py-3">
                    Agent Basecamp writes (Cursor / Claude via hosted MCP) also need Connect
                    here. MCP config lives under{' '}
                    <strong className="font-medium text-foreground">Account</strong> settings.
                  </p>
                  {basecampJustDisconnected || !basecampAuth?.authenticated ? (
                    <div className="space-y-4 rounded-lg border border-border bg-muted/20 px-5 py-5">
                      <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>Not connected. Connect your Basecamp login once to use it on any workspace.</span>
                      </div>
                      <Button
                        data-testid={testIds.workspaceSettings.basecampConnectButton}
                        onClick={() => {
                          const url = basecampAuth?.auth_url || `${API_BASE_URL}/auth/basecamp`;
                          window.open(url, 'basecamp-auth', 'width=600,height=700');
                        }}
                      >
                        Connect Basecamp
                      </Button>
                    </div>
                  ) : (
                    <>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/20 px-5 py-4">
                    <Badge
                      variant="outline"
                      className="border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400 gap-1.5 px-2.5 py-1 text-xs font-medium"
                    >
                      <Check className="h-3.5 w-3.5 shrink-0" />
                      OAuth connected
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={basecampDisconnecting}
                      onClick={async () => {
                        setBasecampDisconnecting(true)
                        try {
                          const res = await fetchWithAuth('/api/auth/basecamp/disconnect', {
                            method: 'POST',
                            credentials: 'include',
                          })
                          if (res.ok) {
                            setBasecampJustDisconnected(true)
                            await refetchBasecampAuth()
                            onSaved?.()
                            toast.success('Basecamp disconnected')
                          } else {
                            const err = await res.json().catch(() => ({}))
                            toast.error(
                              typeof err?.error === 'string'
                                ? err.error
                                : 'Could not disconnect Basecamp'
                            )
                          }
                        } finally {
                          setBasecampDisconnecting(false)
                        }
                      }}
                    >
                      {basecampDisconnecting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Disconnect'
                      )}
                    </Button>
                  </div>

                  {basecampAuth?.authenticated && !basecampJustDisconnected && basecampRow?.enabled && (() => {
                    const linkedAccountId = basecampRow?.config?.account_id
                    const linkedAccountName = basecampRow?.config?.account_name
                    const canConfigure = currentOrg?.role === 'admin' || currentOrg?.role === 'owner'

                    const discoverAccounts = async () => {
                      setBcAccountsLoading(true)
                      try {
                        const res = await fetchWithAuth('/api/basecamp/accounts', { credentials: 'include' })
                        if (res.ok) {
                          const data = await res.json()
                          setBcAccounts(data.accounts || [])
                        }
                      } catch (e) {
                        console.error('Failed to discover BC accounts:', e)
                      } finally {
                        setBcAccountsLoading(false)
                      }
                    }

                    const linkAccount = async (accountId: number, accountName: string) => {
                      setBcAccountSaving(true)
                      try {
                        await updateExtensionConfig('basecamp', {
                          ...basecampRow.config,
                          account_id: String(accountId),
                          account_name: accountName,
                        })
                        setBcAccounts([])
                        toast.success(`Linked to ${accountName}`)
                        onSaved?.()
                      } catch (e) {
                        toast.error('Failed to link Basecamp account')
                      } finally {
                        setBcAccountSaving(false)
                      }
                    }

                    return (
                      <div className="rounded-lg border border-border bg-muted/20 px-5 py-5 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">
                              Linked Basecamp account
                            </p>
                            <p className="text-sm font-medium">
                              {linkedAccountName || linkedAccountId || 'None selected'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {linkedAccountId
                                ? 'Workspace data is scoped to this organization.'
                                : canConfigure
                                  ? 'No Basecamp organization linked - select one to scope this workspace data'
                                  : 'No Basecamp organization linked - ask a workspace admin to configure this'}
                            </p>
                          </div>
                          {canConfigure ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              disabled={bcAccountsLoading}
                              onClick={discoverAccounts}
                            >
                              {bcAccountsLoading ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : linkedAccountId ? (
                                'Change'
                              ) : (
                                'Select account'
                              )}
                            </Button>
                          ) : (
                            <p className="text-xs text-muted-foreground italic shrink-0">Admin required</p>
                          )}
                        </div>

                        {bcAccounts.length > 0 && (
                          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
                            {bcAccounts.map((acct) => (
                              <li key={acct.id}>
                                <button
                                  disabled={bcAccountSaving}
                                  className="flex w-full items-center justify-between px-3 py-3 text-sm hover:bg-accent transition-colors text-left"
                                  onClick={() => linkAccount(acct.id, acct.name)}
                                >
                                  <span>{acct.name}</span>
                                  {String(acct.id) === linkedAccountId ? (
                                    <Badge variant="secondary" className="text-xs">Current</Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">ID: {acct.id}</span>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )
                  })()}

                  {basecampAuth?.authenticated && !basecampJustDisconnected && currentOrg?.id && (
                    <BasecampProjectFolderMappingsCard
                      organizationId={currentOrg.id}
                      canEdit={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                    />
                  )}

                  {basecampAuth?.authenticated && !basecampJustDisconnected && currentOrg?.id && (
                    <BasecampListeningPanel organizationId={currentOrg.id} />
                  )}
                    </>
                  )}
                </div>
                </ExtensionSettingsShell>
              )}

              {/* Google Drive extension page */}
              {extensionId === 'google-drive' && (
                <ExtensionSettingsShell
                  title="Google Drive"
                  description="Connect Google accounts once at the user level. Import Docs, sync comments, and push edits from workspaces with Drive enabled."
                  enabled={Boolean(googleDriveRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'google-drive'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('google-drive')
                    try {
                      await toggleExtension('google-drive', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('google-drive')}
                >
                <Card className="border-0 bg-muted/30">
                  <CardHeader>
                    <CardTitle className="text-base">Google Drive accounts</CardTitle>
                    <CardDescription>
                      Accounts are user-level. Pick the active account for this workspace when you have more than one.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loadingGoogleAccounts ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading connected accounts...
                      </div>
                    ) : googleAccounts.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No Google accounts connected yet.
                      </p>
                    ) : (
                      <div className="divide-y">
                        {googleAccounts.map((account) => (
                          <div key={account.id} className="flex items-center justify-between py-2">
                            <div>
                              <p className="text-sm font-medium">{account.email ?? 'Unknown account'}</p>
                              <p className="text-xs text-muted-foreground">
                                Connected {formatRelativeDate(account.connectedAt)}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={disconnectingGoogleAccountId === account.id}
                              data-testid={testIds.workspaceSettings.googleDriveDisconnectButton}
                              onClick={async () => {
                                setDisconnectingGoogleAccountId(account.id)
                                try {
                                  const res = await fetchWithAuth(
                                    `/api/google-drive/accounts/${account.id}`,
                                    { method: 'DELETE' },
                                  )
                                  if (res.ok) {
                                    setGoogleAccounts((prev) => prev.filter((a) => a.id !== account.id))
                                    await refetchGoogleDriveAuth()
                                    onSaved?.()
                                    toast.success('Google account disconnected')
                                  } else {
                                    const err = await res.json().catch(() => ({}))
                                    toast.error(
                                      typeof err?.error === 'string'
                                        ? err.error
                                        : 'Could not disconnect Google account',
                                    )
                                  }
                                } finally {
                                  setDisconnectingGoogleAccountId(null)
                                }
                              }}
                            >
                              {disconnectingGoogleAccountId === account.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Disconnect'
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Workspace account assignment — only show when multiple accounts exist */}
                    {googleAccounts.length > 1 && currentOrg && (
                      <div className="border-t pt-4">
                        <p className="mb-1.5 text-sm font-medium">Active account for this workspace</p>
                        <p className="mb-2 text-xs text-muted-foreground">
                          Choose which Google account is used when browsing Drive in this workspace.
                        </p>
                        <div className="flex items-center gap-2">
                          <Select
                            value={workspaceGoogleTokenId ?? '__default__'}
                            onValueChange={async (tokenId) => {
                              setAssigningGoogleAccount(true)
                              try {
                                const effectiveId = tokenId === '__default__' ? null : tokenId
                                if (effectiveId) {
                                  const res = await fetchWithAuth('/api/google-drive/workspace-account', {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ tokenId: effectiveId, organizationId: currentOrg.id }),
                                  })
                                  if (res.ok) {
                                    setWorkspaceGoogleTokenId(effectiveId)
                                    toast.success('Workspace Google account updated')
                                  } else {
                                    const err = await res.json().catch(() => ({}))
                                    toast.error(typeof err?.error === 'string' ? err.error : 'Failed to update')
                                  }
                                } else {
                                  // Clear workspace assignment — fall back to default (most recent token)
                                  const res = await fetchWithAuth('/api/google-drive/workspace-account/clear', {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ organizationId: currentOrg.id }),
                                  })
                                  if (res.ok) {
                                    setWorkspaceGoogleTokenId(null)
                                    toast.success('Workspace Google account reset to default')
                                  }
                                }
                              } finally {
                                setAssigningGoogleAccount(false)
                              }
                            }}
                          >
                            <SelectTrigger className="w-64">
                              <SelectValue placeholder="Select account..." />
                              {assigningGoogleAccount && <Loader2 className="ml-2 h-3 w-3 animate-spin" />}
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__default__">
                                <span className="text-muted-foreground">Default (most recently connected)</span>
                              </SelectItem>
                              {googleAccounts.map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.email ?? account.label ?? 'Unknown account'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={googleDriveConnecting}
                      data-testid={testIds.workspaceSettings.googleDriveConnectButton}
                      onClick={async () => {
                        setGoogleDriveConnecting(true)
                        try {
                          const orgQs = currentOrg?.id
                            ? `?organizationId=${encodeURIComponent(currentOrg.id)}`
                            : ''
                          const res = await fetchWithAuth(`/api/google-drive/auth/url${orgQs}`)
                          if (!res.ok) {
                            const err = await res.json().catch(() => ({}))
                            const missing = Array.isArray(err?.missing)
                              ? (err.missing as string[]).join(', ')
                              : ''
                            toast.error(
                              typeof err?.error === 'string'
                                ? err.error
                                : 'Could not start Google Drive auth',
                              missing
                                ? { description: `Server missing: ${missing}. Set on Railway and redeploy.` }
                                : undefined,
                            )
                            return
                          }
                          const data = (await res.json()) as { url?: string }
                          if (!data.url) {
                            toast.error('Google Drive auth URL missing')
                            return
                          }
                          window.open(data.url, 'google-drive-auth', 'width=600,height=700')
                        } catch (e) {
                          console.error('Google Drive connect failed:', e)
                          toast.error('Could not start Google Drive auth')
                        } finally {
                          setGoogleDriveConnecting(false)
                        }
                      }}
                    >
                      {googleDriveConnecting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : googleAccounts.length === 0 ? (
                        'Connect Google account'
                      ) : (
                        'Add another account'
                      )}
                    </Button>
                  </CardContent>
                </Card>
                {currentOrg?.id ? (
                  <DrivePublishTemplateCard
                    organizationId={currentOrg.id}
                    canEdit={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  />
                ) : null}
                </ExtensionSettingsShell>
              )}

              {/* Zoom extension page */}
              {extensionId === 'zoom' && (
                <ExtensionSettingsShell
                  title="Zoom"
                  description="Auto-capture meeting transcripts via webhook and run meeting-notes flows."
                  enabled={Boolean(zoomRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'zoom'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('zoom')
                    try {
                      await toggleExtension('zoom', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('zoom')}
                >
                <ZoomIntegrationSection
                  organizationId={currentOrg?.id}
                  workspaceName={currentOrg?.name}
                />
                </ExtensionSettingsShell>
              )}

              {/* SMTP extension page */}
              {extensionId === 'smtp' && (
                <ExtensionSettingsShell
                  title="SMTP"
                  description="Tenant-owned outbound mail for flows (email.send / email.send_batch). Not platform Resend."
                  enabled={Boolean(smtpRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'smtp'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('smtp')
                    try {
                      await toggleExtension('smtp', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('smtp')}
                >
                <SmtpIntegrationSection organizationId={currentOrg?.id} />
                </ExtensionSettingsShell>
              )}

              {extensionId === 'uptime' && (
                <ExtensionSettingsShell
                  title="Uptime"
                  description="Read checks, outages, and alerts from Uptime.com. Slack stays the alert channel."
                  enabled={Boolean(uptimeRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'uptime'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('uptime')
                    try {
                      await toggleExtension('uptime', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('uptime')}
                >
                <UptimeIntegrationSection
                  organizationId={currentOrg?.id}
                  canManage={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                />
                </ExtensionSettingsShell>
              )}

              {extensionId === 'nextcloud' && (
                <ExtensionSettingsShell
                  title="Nextcloud"
                  description="Connect a Nextcloud instance. Browse and import come next. Sign-in uses Login Flow, not your account password."
                  enabled={Boolean(nextcloudRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'nextcloud'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('nextcloud')
                    try {
                      await toggleExtension('nextcloud', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('nextcloud')}
                >
                <NextcloudIntegrationSection
                  organizationId={currentOrg?.id}
                  canManage={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  enabled={Boolean(nextcloudRow?.enabled)}
                  onSaveConfig={async (config) => {
                    await updateExtensionConfig('nextcloud', {
                      ...(nextcloudRow?.config || {}),
                      ...config,
                    })
                  }}
                />
                </ExtensionSettingsShell>
              )}

              {extensionId === 'slack' && (
                <ExtensionSettingsShell
                  title="Slack"
                  description="Read one public channel, its threads, and reactions. Slack stays the alert channel."
                  enabled={Boolean(slackRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'slack'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('slack')
                    try {
                      await toggleExtension('slack', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('slack')}
                >
                <SlackIntegrationSection
                  organizationId={currentOrg?.id}
                  canManage={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                />
                </ExtensionSettingsShell>
              )}

              {/* Cursor IDE extension page */}
              {extensionId === 'cursor' && (
                <ExtensionSettingsShell
                  title="Cursor IDE"
                  description="Open Maater documents in Cursor and sync edits back. Prefer a named .code-workspace per client."
                  enabled={Boolean(cursorRow?.enabled)}
                  canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                  toggling={extensionToggling === 'cursor'}
                  onEnabledChange={async (checked) => {
                    setExtensionToggling('cursor')
                    try {
                      await toggleExtension('cursor', checked)
                      onSaved?.()
                    } catch (e) {
                      console.error('Failed to toggle extension:', e)
                    } finally {
                      setExtensionToggling(null)
                    }
                  }}
                  data-testid={testIds.workspaceSettings.extensionPage('cursor')}
                >
                <Card className="border-0 bg-muted/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Terminal className="h-4 w-4" />
                      Cursor IDE access tokens
                    </CardTitle>
                    <CardDescription>
                      Install the Cursor extension, then sign in via Maater: Connect (opens app.maater.ai)
                      or paste a token into ~/.flownotes/.cursor-token. API URL and web login URL are different hosts.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-md border bg-background/50 p-4 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Download the Cursor extension (.vsix) to install Maater in Cursor without cloning the
                        monorepo.
                      </p>
                      <Button variant="secondary" size="sm" asChild>
                        <a
                          href={`${API_BASE_URL}/api/extensions/cursor/download`}
                          download={cursorVsixFilename ?? undefined}
                          data-testid={testIds.workspaceSettings.cursorExtensionVsixDownload}
                        >
                          {cursorVsixFilename ? `Download ${cursorVsixFilename}` : 'Download extension (.vsix)'}
                        </a>
                      </Button>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p className="font-medium text-foreground">Installation</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>In Cursor: Extensions panel ... menu - Install from VSIX (reinstall after each download)</li>
                          <li>
                            CMD+P:{' '}
                            <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">Maater: Connect</code>
                            {' '}
                            opens app.maater.ai/connect/cursor (not the API URL below)
                          </li>
                          <li>
                            Or generate a token below and save to{' '}
                            <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">~/.flownotes/.cursor-token</code>
                            , then reload Cursor (no Connect needed)
                          </li>
                        </ol>
                      </div>
                    </div>

                    {/* Generate new token */}
                    <div className="flex items-center gap-2">
                      <Input
                        value={cursorTokenName}
                        onChange={(e) => setCursorTokenName(e.target.value)}
                        placeholder="Token name (e.g. Cursor on MacBook)"
                        className="flex-1 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={generateCursorToken}
                        disabled={cursorGenerating || !cursorTokenName.trim()}
                      >
                        {cursorGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate'}
                      </Button>
                    </div>

                    {/* Show new token once */}
                    {cursorNewToken && (
                      <div className="flex items-center gap-2 rounded-md bg-green-500/10 border border-green-500/20 p-3">
                        <code className="flex-1 text-xs font-mono text-green-600 break-all select-all">
                          {cursorNewToken}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => {
                            navigator.clipboard.writeText(cursorNewToken)
                            toast.success('Token copied')
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}

                    {/* Existing tokens */}
                    {cursorTokensLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading tokens…
                      </div>
                    ) : cursorTokens.length > 0 ? (
                      <div className="space-y-1">
                        {cursorTokens.map((token) => (
                          <div key={token.id} className="flex items-center justify-between rounded-md px-3 py-2 bg-background border text-sm">
                            <div>
                              <p className="font-medium">{token.name}</p>
                              <p className="text-xs text-muted-foreground">
                                Created {token.created_at ? new Date(token.created_at).toLocaleDateString() : '—'}
                                {token.last_used_at && ` · Last used ${new Date(token.last_used_at).toLocaleDateString()}`}
                                {token.expires_at && ` · Expires ${new Date(token.expires_at).toLocaleDateString()}`}
                              </p>
                              {token.scopes && token.scopes.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Scopes: {token.scopes.join(', ')}
                                </p>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              disabled={cursorRevokingId === token.id}
                              onClick={() => revokeCursorToken(token.id)}
                            >
                              {cursorRevokingId === token.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />
                              }
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No tokens yet. Generate one above.</p>
                    )}

                    <p className="text-xs text-muted-foreground">
                      API URL for sync (extension setting <code className="font-mono">flownotes.apiUrl</code>):{' '}
                      <code className="font-mono">{API_BASE_URL}</code>
                      <br />
                      Login URL (extension setting <code className="font-mono">flownotes.webUrl</code>):{' '}
                      <code className="font-mono">https://app.maater.ai</code>
                    </p>
                  </CardContent>
                </Card>
                </ExtensionSettingsShell>
              )}

              {extensionId &&
                !(EXTENSIONS_WITH_SETUP as readonly string[]).includes(extensionId) && (
                  <ExtensionSettingsShell
                    title={EXTENSION_LABELS[extensionId] ?? extensionId}
                    description={EXTENSION_DESCRIPTIONS[extensionId]}
                    enabled={Boolean(
                      extensions.find((e) => e.extension_id === extensionId)?.enabled,
                    )}
                    canToggle={currentOrg.role === 'admin' || currentOrg.role === 'owner'}
                    toggling={extensionToggling === extensionId}
                    onEnabledChange={async (checked) => {
                      setExtensionToggling(extensionId)
                      try {
                        await toggleExtension(extensionId, checked)
                        onSaved?.()
                      } catch (e) {
                        console.error('Failed to toggle extension:', e)
                      } finally {
                        setExtensionToggling(null)
                      }
                    }}
                    data-testid={testIds.workspaceSettings.extensionPage(extensionId)}
                  >
                    <p className="text-sm text-muted-foreground">
                      No additional setup for this extension yet. Toggle it on the Extensions list.
                    </p>
                  </ExtensionSettingsShell>
                )}
            </div>
          </TooltipProvider>
          )}
        </div>
      )}
      </div>
    </div>
  )
}
